import mapStyles from '../json/map-settings.json';
import createHtmlMarkerClass from './html-marker';

const isGoogleMapsReady = () =>
    window.google &&
    window.google.maps &&
    typeof window.google.maps.Map === 'function';

const loadGoogleMaps = src => {
    if (isGoogleMapsReady()) return Promise.resolve();
    if (!src) return Promise.reject(new Error('Missing Google Maps API URL'));
    if (window.__struxiGoogleMapsLoader) {
        return window.__struxiGoogleMapsLoader;
    }

    window.__struxiGoogleMapsLoader = new Promise((resolve, reject) => {
        const callbackName = '__struxiGoogleMapsLoaded';
        const script = document.createElement('script');
        const url = new URL(src, window.location.href);

        url.searchParams.set('callback', callbackName);
        script.src = url.toString();
        script.async = true;
        script.dataset.googleMapsApi = '';

        window[callbackName] = () => {
            delete window[callbackName];
            resolve();
        };

        script.addEventListener('error', () => {
            delete window[callbackName];
            delete window.__struxiGoogleMapsLoader;
            reject(new Error('Unable to load Google Maps API'));
        });

        document.head.appendChild(script);
    });

    return window.__struxiGoogleMapsLoader;
};

export default class GoogleMap {
    constructor(el, firstOpened = false, centerAtFirstPoint = false) {
        if (!el) return;

        this.el = el;
        this.firstOpened = firstOpened;
        this.centerAtFirstPoint = centerAtFirstPoint;
        this.points = this.getPoints();
        this.settings = this.getSettings();

        this.map = null;
        this.markers = [];
        this.styles = mapStyles;

        this.init();
    }

    getPoints() {
        try {
            return JSON.parse(this.el.dataset.map || '[]');
        } catch (e) {
            console.error('Invalid JSON in data-map', e);
            return [];
        }
    }

    getSettings() {
        try {
            const settings = JSON.parse(this.el.dataset.settings || '{}');

            if (
                !settings ||
                Array.isArray(settings) ||
                typeof settings !== 'object'
            ) {
                return {};
            }

            return settings;
        } catch (e) {
            console.error('Invalid JSON in data-settings', e);
            return {};
        }
    }

    async init() {
        try {
            await this.waitForViewport();
            await loadGoogleMaps(this.el.dataset.googleMapsUrl);
        } catch (error) {
            console.error(error);
            return;
        }

        this.pinBorder = this.el.dataset.mapPinBorder;
        this.defaultPin = this.el.dataset.mapDefaultPin;
        this.singlePointPinBorder = this.el.dataset.mapSinglePointPinBorder;
        this.HtmlMarker = createHtmlMarkerClass(window.google);

        this.initMap();
        this.addMarkers();
    }

    waitForViewport() {
        if (!('IntersectionObserver' in window)) return Promise.resolve();

        return new Promise(resolve => {
            const observer = new IntersectionObserver(entries => {
                if (entries.some(entry => entry.isIntersecting)) {
                    observer.disconnect();
                    resolve();
                }
            });

            observer.observe(this.el);
        });
    }

    initMap() {
        const defaultCenter = this.points.single_point
            ? {
                  lat: parseFloat(this.points.single_point.lat) + 0.02,
                  lng: parseFloat(this.points.single_point.lng),
              }
            : this.centerAtFirstPoint && this.points.length
            ? {
                  lat: parseFloat(this.points[0].lat),
                  lng: parseFloat(this.points[0].lon),
              }
            : { lat: 50.6695471, lng: 17.9290346 };

        const defaultOptions = {
            center: defaultCenter,
            zoom: this.centerAtFirstPoint ? 13 : 12,
            styles: this.styles.length ? this.styles : undefined,
        };

        this.map = new google.maps.Map(this.el, {
            ...defaultOptions,
            ...this.settings,
        });
    }

    addMarkers() {
        if (this.points.single_point) {
            const { single_point: singlePoint } = this.points;
            this.infoWindow = new google.maps.InfoWindow();

            this.map.addListener('click', () => {
                this.infoWindow.close();
            });
            if (!singlePoint.lat || !singlePoint.lng) return;
            singlePoint.lon = singlePoint.lng;
            const marker = this.createMarker(singlePoint, 'singlePoint');
            this.markers.push(marker);
        } else {
            if (!this.points.length) return;

            this.infoWindow = new google.maps.InfoWindow();

            this.map.addListener('click', () => {
                this.infoWindow.close();
            });

            this.points.forEach(point => {
                if (!point.lat || !point.lon) return;

                const { poi_points } = point;
                const marker = this.createMarker(point);

                this.markers.push(marker);

                if (poi_points) {
                    poi_points.forEach(poi_point => {
                        const poi_marker = this.createMarker(poi_point, 'poi');
                        this.markers.push(poi_marker);
                    });
                }
            });

            if (this.markers.length && this.firstOpened) {
                google.maps.event.trigger(this.markers[0], 'click');
            }
        }
    }

    createMarker(point, type = 'default') {
        const marker = new this.HtmlMarker({
            map: this.map,
            position: {
                lat: parseFloat(point.lat),
                lng: parseFloat(point.lon),
            },
            content: this.createMarkerHTML(point, type),
            onClick: () => {
                if (type === 'singlePoint') return;
                this.infoWindow.setContent(
                    this.createTooltipContent(point, type)
                );

                if (type === 'poi') {
                    this.infoWindow.setOptions({
                        pixelOffset: new google.maps.Size(0, -85),
                    });
                } else {
                    this.infoWindow.setOptions({
                        pixelOffset: new google.maps.Size(0, 0),
                    });
                }

                this.infoWindow.setPosition({
                    lat: parseFloat(point.lat),
                    lng: parseFloat(point.lon),
                });

                this.infoWindow.open(this.map);

                google.maps.event.addListenerOnce(
                    this.infoWindow,
                    'domready',
                    () => {
                        const iw = document.querySelector('.gm-style-iw-t');

                        if (iw) {
                            iw.classList.add('marker-tooltip-wrapper');
                            iw.classList.add(`-${type}`);
                        }
                    }
                );
            },
        });

        return marker;
    }

    createTooltipContent(point, type = 'default') {
        if (type === 'poi') {
            return `
                <div class="marker-tooltip -poi block max-w-[266px]">
                    <div class="flex flex-col items-center px-[12px] py-[8px]">
                        <div class="text-regular">${point.name || ''}</div>
                    </div>
                </div>`;
        } else if (type == 'singlePoint') {
            return '';
        } else {
            let logo = point.logo;

            return `
                <a href="${point.url}" class="marker-tooltip block w-[266px]">
                    <div class="flex flex-col items-center p-[18px]">
                        ${
                            logo
                                ? `<div class="mb-12">
                            <img class="!max-w-[38px]" src="${logo}" />
                        </div>`
                                : ''
                        }
                        <h5 class="">${point.title || ''}</h5>
                        ${
                            point.address
                                ? `<p class="text-regular text-gray-80 m-0">${point.address}</p>`
                                : ''
                        }
                    </div>
                </a>
            `;
        }
    }

    createMarkerHTML(point, type = 'default') {
        let markerClass = '';

        if (type === 'poi') {
            const poiType = point['type'];

            const pinLogo = point.icon;
            markerClass = `-poi -${poiType}`;

            return `
                <div class="marker-pin ${markerClass}">
                    <div class="marker-border">
                        <img src="${this.pinBorder}" />
                    </div>
                    <div class="marker-circle">
                        <img src="${pinLogo}" alt="">
                    </div>
                </div>
            `;
        }

        if (type === 'singlePoint') {
            const {
                pin_image: image,
                pin_name: name,
                pin_address: address,
            } = point;
            return `
                <div class="marker-pin ${markerClass}">
                    <div class="marker-border flex items-center justify-center relative">
                        <div class="relative">
                            <img src="${
                                this.singlePointPinBorder
                            }" class="max-desktop:w-[85vw] shadow-[32px_-16px_32px_0px_#00000029]" />
                            <div class="p-12 absolute left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2 w-full flex flex-col gap-12 h-full text-center">
                                ${
                                    image
                                        ? `<img src="${image}" class="w-full rounded-small" alt="">`
                                        : ''
                                }
                                <div>
                                    ${name ? `<p class="h5">${name}</p>` : ''}
                                    ${
                                        address
                                            ? `<p class="text-regular text-gray-80">${address}</p>`
                                            : ''
                                    }
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            `;
        } else {
            if (!this.defaultPin) {
                return `
                    <div class="marker-pin ${markerClass}">
                        <div class="marker-border">
                            <img src="${this.pinBorder}" />
                        </div>
                        <div class="marker-circle">
                            <img src="${point.pin}" alt="">
                        </div>
                    </div>
                `;
            }

            return `
                <div class="marker-pin ${markerClass}">
                    <img src="${this.defaultPin}" alt="">
                </div>
            `;
        }
    }
}
